const servicePages = {
  aiDx: {
    slug: "ai-dx",
    canonical: "https://www.napire.co.jp/ai-dx.html",
    pageTitle: {
      ja: "法人向けAI・DX導入支援 | 株式会社NAPIRE",
      en: "Enterprise AI & DX Implementation | NAPIRE Inc.",
    },
    metaDescription: {
      ja: "NAPIREの法人向けAI・DX導入支援。生成AI・RAG、画像認識、需要予測からデータ基盤・MLOpsまで。PoCで終わらせず、現場で使われるAIを設計・実装します。",
      en: "NAPIRE helps enterprises ship AI and DX—from GenAI/RAG and computer vision to forecasting, data platforms, and MLOps. We build systems that survive production, not just PoC decks.",
    },
    keywords: {
      ja: "AI導入支援,DX,生成AI,RAG,LLM,機械学習,MLOps,データ基盤,法人向け,コンサルティング,NAPIRE",
      en: "AI implementation,DX,GenAI,RAG,LLM,machine learning,MLOps,data platform,enterprise,NAPIRE",
    },
    hero: {
      eyebrow: { ja: "Enterprise AI / DX", en: "Enterprise AI / DX" },
      title: {
        ja: "AIは、導入した瞬間から始まる。",
        en: "AI starts the moment you ship.",
      },
      lead: {
        ja: "ツールを入れただけでは、現場は動きません。NAPIREは課題の輪郭づけからアルゴリズム選定、本番運用・ガバナンスまで一気通貫で伴走。自社AIドローンで培った「ハードとソフトを同じチームで握る」実装力を、法人のDXにも活かします。",
        en: "Buying tools doesn't move the floor. NAPIRE partners from problem framing through model choice, production ops, and governance—the same hardware–software discipline we use on our own AI drones, applied to your DX.",
      },
      cta: { ja: "無料相談を予約", en: "Book a free consult" },
    },
    questions: {
      heading: { ja: "こんな違和感、ありませんか？", en: "Sound familiar?" },
      items: [
        {
          q: {
            ja: "PoCは成功したのに、本番に乗らない",
            en: "The PoC worked—production never did",
          },
          a: {
            ja: "データ品質・権限・運用フローを最初から設計に組み込み、スケール前提のアーキテクチャで実装します。",
            en: "We bake data quality, access, and ops into the architecture from day one—so scale isn't a surprise retrofit.",
          },
        },
        {
          q: {
            ja: "生成AIを入れたが、誰も使っていない",
            en: "GenAI landed—nobody uses it",
          },
          a: {
            ja: "ユースケースを業務フローに落とし込み、RAG・プロンプト・評価指標まで含めて「使われる形」に仕上げます。",
            en: "We anchor use cases in real workflows—RAG, prompts, and evaluation metrics included—so adoption isn't wishful thinking.",
          },
        },
        {
          q: {
            ja: "ベンダー任せで、社内にノウハウが残らない",
            en: "Vendors leave—no capability stays behind",
          },
          a: {
            ja: "設計判断のドキュメント化とハンズオン移管を標準化。次の改善サイクルを自走できる状態を目指します。",
            en: "We document design decisions and run hands-on handoffs so your team can own the next improvement loop.",
          },
        },
      ],
    },
    services: {
      heading: { ja: "提供領域", en: "What we deliver" },
      items: [
        {
          title: { ja: "生成AI / LLM・RAG", en: "GenAI, LLMs & RAG" },
          desc: {
            ja: "社内ナレッジ検索、業務支援チャット、文書生成。ハルシネーション抑制とコスト設計を両立したエンタープライズ向け実装。",
            en: "Internal knowledge search, copilots, document generation—enterprise builds that tame hallucination risk and inference cost.",
          },
        },
        {
          title: { ja: "Computer Vision / OCR", en: "Computer vision & OCR" },
          desc: {
            ja: "画像検知・分類・構造化。現場カメラやドキュメントからの自動抽出を、精度と運用コストのバランスで設計。",
            en: "Detection, classification, structuring—from field cameras and documents—with accuracy traded honestly against ops cost.",
          },
        },
        {
          title: { ja: "予測・最適化AI", en: "Forecasting & optimization" },
          desc: {
            ja: "需要予測、在庫・価格最適化、異常検知。ドリフト監視と再学習設計まで含めたMLOps基盤を構築。",
            en: "Demand forecasting, inventory and pricing optimization, anomaly detection—with MLOps for drift monitoring and refresh.",
          },
        },
        {
          title: { ja: "データ基盤・分析", en: "Data platforms & analytics" },
          desc: {
            ja: "ログ・販売・設備データの統合、特徴設計、ダッシュボード。AWS / GCP / Azure 上で学習ループが回る形を設計。",
            en: "Unify logs, sales, and equipment data—features and dashboards on AWS/GCP/Azure where models keep learning.",
          },
        },
      ],
    },
    process: {
      heading: { ja: "導入の流れ", en: "How we work" },
      steps: [
        {
          title: { ja: "01 課題の輪郭づけ", en: "01 Frame the problem" },
          desc: {
            ja: "KPI・制約・データの在り方を整理。ROIが見えるユースケースに絞り込みます。",
            en: "Align on KPIs, constraints, and data reality—narrowing to use cases with visible ROI.",
          },
        },
        {
          title: { ja: "02 設計・PoC", en: "02 Design & PoC" },
          desc: {
            ja: "アルゴリズム選定とアーキテクチャ設計。短期間で仮説検証し、本番前提の判断材料を揃えます。",
            en: "Pick algorithms and architecture—fast hypothesis tests that inform production decisions.",
          },
        },
        {
          title: { ja: "03 本番実装", en: "03 Production build" },
          desc: {
            ja: "API・UI・権限・監視まで含めた実装。セキュリティと運用を最初から織り込みます。",
            en: "Ship APIs, UI, access, and monitoring—security and ops woven in from the start.",
          },
        },
        {
          title: { ja: "04 運用・改善", en: "04 Operate & improve" },
          desc: {
            ja: "精度モニタリング、再学習、フィードバックループ。現場の変化に追従し続ける仕組みを残します。",
            en: "Accuracy monitoring, retraining, feedback loops—systems that evolve with the field.",
          },
        },
      ],
    },
    why: {
      heading: { ja: "NAPIREが選ばれる理由", en: "Why NAPIRE" },
      items: [
        {
          title: { ja: "実装まで握るAIチーム", en: "Builders, not slide decks" },
          body: {
            ja: "研究動向の理解とプロダクション実装を同じチームが担当。コンサルで終わらず、コードが動くところまで責任を持ちます。",
            en: "One squad spans research literacy and production code—we don't stop at recommendations.",
          },
        },
        {
          title: { ja: "自社プロダクトで鍛えた実装力", en: "Hardened on our own products" },
          body: {
            ja: "自社AIドローンの機体設計からエッジ推論まで一気通貫で開発。物理制約の中で動くAIの設計ノウハウが、法人案件にも活きます。",
            en: "We ship our own AI drones end to end—edge inference under physical limits informs every enterprise build.",
          },
        },
        {
          title: { ja: "説明可能で保守できる形", en: "Explainable & maintainable" },
          body: {
            ja: "ブラックボックスに頼らず、意思決定者と現場の双方が理解できるドキュメントと設計を残します。",
            en: "No mystery boxes—artifacts that executives and operators can both understand and extend.",
          },
        },
      ],
    },
    faq: {
      heading: { ja: "よくあるご質問", en: "FAQ" },
      items: [
        {
          q: { ja: "どのような業界に対応していますか？", en: "Which industries do you serve?" },
          a: {
            ja: "製造、小売、エネルギー、公共、農業など幅広く対応。業界固有のデータと規制を踏まえた設計を行います。",
            en: "Manufacturing, retail, energy, public sector, agriculture, and more—designs that respect domain data and regulation.",
          },
        },
        {
          q: { ja: "小規模なPoCから始められますか？", en: "Can we start with a small PoC?" },
          a: {
            ja: "はい。4〜8週間程度のスコープで仮説検証から始め、成果が見えた段階で本番化を検討する進め方が可能です。",
            en: "Yes—4–8 week scoped pilots are common; we scale to production when signals are clear.",
          },
        },
        {
          q: { ja: "既存のクラウド環境を使えますか？", en: "Can you use our existing cloud?" },
          a: {
            ja: "AWS、GCP、Azure いずれにも対応。お客様のガバナンスポリシーに沿った構成を提案します。",
            en: "We work across AWS, GCP, and Azure—architectures aligned with your governance policies.",
          },
        },
        {
          q: { ja: "社内にAI専門人材が少なくても依頼できますか？", en: "Can you help if we lack in-house AI talent?" },
          a: {
            ja: "はい。設計判断のドキュメント化とハンズオン移管を標準化し、社内チームが運用・改善を継続できる状態を目指します。",
            en: "Yes—we document decisions and run hands-on handoffs so your team can own ongoing operations and improvements.",
          },
        },
        {
          q: { ja: "守秘義務・NDAはどのように扱いますか？", en: "How do you handle confidentiality and NDAs?" },
          a: {
            ja: "標準NDAの締結に対応しています。お客様データはプロジェクト専用環境で扱い、許可なく第三者へ共有しません。",
            en: "We sign standard NDAs, use project-isolated environments, and never share customer data without approval.",
          },
        },
        {
          q: { ja: "開発期間の目安はどのくらいですか？", en: "How long do projects typically take?" },
          a: {
            ja: "PoCは4〜8週間、本番実装は3〜6ヶ月が目安です。スコープとデータ準備状況により変動します。",
            en: "PoCs run 4–8 weeks; production builds often 3–6 months—depending on scope and data readiness.",
          },
        },
        {
          q: { ja: "オンプレミス環境でも対応できますか？", en: "Do you support on-premises environments?" },
          a: {
            ja: "可能です。セキュリティ要件によりクラウド利用が難しい場合も、オンプレ・閉域ネットワーク向けの構成を提案します。",
            en: "Yes—for strict security requirements we design for on-prem and closed-network deployments.",
          },
        },
        {
          q: { ja: "生成AIのハルシネーション対策はどうしていますか？", en: "How do you mitigate GenAI hallucinations?" },
          a: {
            ja: "RAG・根拠提示・許可リスト・人の確認フローを組み合わせ、用途に応じたリスク許容度を設計します。",
            en: "We combine RAG, citations, allowlists, and human review—tuned to each use case's risk tolerance.",
          },
        },
        {
          q: { ja: "費用感を教えてください。", en: "What does it cost?" },
          a: {
            ja: "PoCは数百万円台から、本番構築は要件により異なります。初回相談で概算レンジをお伝えします。",
            en: "PoCs often start in the low millions of yen (JPY); production varies—we share ranges in an initial consult.",
          },
        },
        {
          q: { ja: "導入後のサポート体制はありますか？", en: "Do you offer post-launch support?" },
          a: {
            ja: "運用監視・再学習・改善サイクルを伴走する保守契約に対応。スポット支援のみのご依頼も可能です。",
            en: "We offer managed support for monitoring, retraining, and iteration—or spot engagements as needed.",
          },
        },
      ],
    },
    cta: {
      heading: { ja: "まずは課題の輪郭から", en: "Start with the problem shape" },
      body: {
        ja: "資料は不要です。オンラインで30分、現状とゴールをお聞かせください。",
        en: "No deck required—book 30 minutes and tell us where you are and where you need to go.",
      },
      button: { ja: "AI・DX導入について相談", en: "Discuss AI & DX" },
    },
  },
  saasCloud: {
    slug: "saas-cloud",
    canonical: "https://www.napire.co.jp/saas-cloud.html",
    pageTitle: {
      ja: "SaaS・クラウド導入支援 | 株式会社NAPIRE",
      en: "SaaS & Cloud Implementation | NAPIRE Inc.",
    },
    metaDescription: {
      ja: "NAPIREのSaaS・クラウド導入支援。Microsoft 365・Google Workspace、SaaS・AI・セキュリティ・クラウド製品の仕入れから移行・定着まで。AI企業ならではのデータ連携・自動化視点で、導入後に使われる環境を構築します。",
      en: "NAPIRE delivers SaaS and cloud—from Microsoft 365 and Google Workspace to SaaS, AI, security, and cloud product procurement, migration, and adoption—with an AI-native lens on integration and automation.",
    },
    keywords: {
      ja: "SaaS導入,クラウド導入,Microsoft 365,Google Workspace,GWS,SaaS,AI,セキュリティ,クラウド,製品仕入れ,ライセンス,PC,法人向け,NAPIRE",
      en: "SaaS implementation,cloud migration,Microsoft 365,Google Workspace,GWS,AI,security,procurement,licensing,PC,NAPIRE,enterprise",
    },
    hero: {
      eyebrow: { ja: "SaaS / Cloud", en: "SaaS / Cloud" },
      title: {
        ja: "導入して、使われるまで。",
        en: "Implemented—and actually used.",
      },
      lead: {
        ja: "ライセンスを契約しただけでは、組織は変わりません。NAPIREはMicrosoft 365・Google Workspaceをはじめ、SaaS・AI・セキュリティ・クラウド製品の選定・仕入れから移行、セキュリティ設計、研修・運用定着まで伴走。PC等のIT機器提供も順次対応予定です。",
        en: "Licenses alone don't transform teams. NAPIRE partners from product selection and procurement—Microsoft 365, Google Workspace, SaaS, AI, security, and cloud—through migration, security design, training, and adoption. PC and hardware supply coming soon.",
      },
      cta: { ja: "導入について相談", en: "Talk implementation" },
    },
    questions: {
      heading: { ja: "導入の現場で、よく聞く声", en: "What we hear in the field" },
      items: [
        {
          q: {
            ja: "M365やGWSを入れたが、現場で活用できていない",
            en: "M365 or GWS is live—but teams aren't using it",
          },
          a: {
            ja: "業務フローに沿った設計と段階的な展開計画で、ツールが「義務」ではなく「当たり前」になる運用をつくります。",
            en: "Workflow-aligned design and phased rollouts—so tools feel natural, not mandated.",
          },
        },
        {
          q: {
            ja: "SaaS・AI・セキュリティ製品を複数ベンダーからまとめて仕入れたい",
            en: "We want one partner for SaaS, AI, and security products",
          },
          a: {
            ja: "Microsoft 365・Google Workspace、各種SaaS、AI・セキュリティ・クラウド製品の選定・仕入れから導入・定着まで一括で支援します。",
            en: "We handle selection and procurement for M365, Google Workspace, SaaS, AI, security, and cloud—through rollout and adoption.",
          },
        },
        {
          q: {
            ja: "導入後のサポート体制が弱く、社内に知見が残らない",
            en: "Post-go-live support is thin—knowledge doesn't stay",
          },
          a: {
            ja: "管理者向けハンズオンと運用ドキュメントを標準化。内製化を見据えたナレッジ移転を行います。",
            en: "Hands-on admin training and runbooks—knowledge transfer aimed at self-sufficiency.",
          },
        },
      ],
    },
    services: {
      heading: { ja: "提供領域", en: "What we deliver" },
      items: [
        {
          title: { ja: "プロダクト仕入れ・ライセンス提供", en: "Product procurement & licensing" },
          desc: {
            ja: "Microsoft 365・Google Workspace、各種SaaS、AI・セキュリティ・クラウド製品の選定・仕入れ。利用部門と要件から最適なプランを整理し、過剰ライセンスを抑えた構成を提案します。",
            en: "Procurement for Microsoft 365, Google Workspace, SaaS, AI, security, and cloud products—right-sized plans mapped to departments and requirements.",
          },
        },
        {
          title: { ja: "Microsoft 365 / Google Workspace", en: "Microsoft 365 & Google Workspace" },
          desc: {
            ja: "テナント設計、ライセンス最適化、Teams・SharePoint・Power Platform / Google Workspace 各種サービスの業務適用。セミナー・研修による定着支援も実施。",
            en: "Tenant design, license optimization, Teams/SharePoint/Power Platform and Google Workspace for real work—plus training for adoption.",
          },
        },
        {
          title: { ja: "クラウド移行・IaaS/PaaS", en: "Cloud migration & IaaS/PaaS" },
          desc: {
            ja: "AWS / GCP / Azure への段階的移行。既存システムとの共存、ネットワーク・ID設計を含めたアーキテクチャ策定。",
            en: "Phased moves to AWS/GCP/Azure—coexistence with legacy, network and identity architecture included.",
          },
        },
        {
          title: { ja: "セキュリティ製品・ガバナンス", en: "Security products & governance" },
          desc: {
            ja: "EDR・SASE・IAM等のセキュリティ製品の選定・仕入れから、アクセス制御、ログ監視、コンプライアンス対応まで。AI利用を見据えたデータ分類・権限設計も含めます。",
            en: "Security product selection and procurement—EDR, SASE, IAM—plus access control, logging, compliance, and data classification for AI use.",
          },
        },
        {
          title: { ja: "SaaS・AI ツール連携・自動化", en: "SaaS & AI integration" },
          desc: {
            ja: "Copilot・Gemini等のAI製品導入、API連携、ワークフロー自動化、データパイプライン構築。導入したツール群を一つの業務基盤として統合します。",
            en: "AI product rollout—Copilot, Gemini, and more—plus API wiring, workflow automation, and data pipelines into one operational fabric.",
          },
        },
        {
          title: { ja: "PC・IT機器の提供", en: "PC & hardware supply (coming soon)" },
          desc: {
            ja: "法人向けPC・周辺機器の仕入れ・販売を順次開始予定。クラウド・SaaS環境と合わせた端末構成の提案も行います。",
            en: "Corporate PC and peripheral procurement and sales launching soon—device configurations aligned with your cloud and SaaS stack.",
          },
        },
      ],
    },
    process: {
      heading: { ja: "導入の流れ", en: "How we work" },
      steps: [
        {
          title: { ja: "01 現状把握", en: "01 Assess" },
          desc: {
            ja: "既存システム・業務フロー・セキュリティ要件を棚卸し。ゴールと制約を明確にします。",
            en: "Inventory systems, workflows, and security—clarify goals and constraints.",
          },
        },
        {
          title: { ja: "02 選定・仕入れ", en: "02 Select & procure" },
          desc: {
            ja: "M365・GWS、SaaS・AI・セキュリティ・クラウド製品の選定・仕入れ。利用部門と要件から最適な構成と移行順序を策定します。",
            en: "Product selection and procurement—M365, GWS, SaaS, AI, security, cloud—mapped to departments, requirements, and migration order.",
          },
        },
        {
          title: { ja: "03 構築・移行", en: "03 Build & migrate" },
          desc: {
            ja: "テナント構築、データ移行、連携設定を実施。ダウンタイムを最小化する段階的な切り替えを行います。",
            en: "Tenant setup, data moves, integrations—phased cutovers that minimize downtime.",
          },
        },
        {
          title: { ja: "04 定着・運用", en: "04 Adopt & operate" },
          desc: {
            ja: "研修、運用マニュアル、ヘルプデスク体制の整備。使われ続ける環境を社内に根付かせます。",
            en: "Training, runbooks, helpdesk patterns—adoption that sticks inside your org.",
          },
        },
      ],
    },
    why: {
      heading: { ja: "AI企業だからできる導入支援", en: "Cloud with an AI-native lens" },
      items: [
        {
          title: { ja: "AI連携を見据えた設計", en: "Built for what comes next" },
          body: {
            ja: "単なるツール導入ではなく、将来のAI・データ活用を見据えたデータ配置と権限設計を最初から行います。",
            en: "We place data and permissions for tomorrow's AI—not just today's licenses.",
          },
        },
        {
          title: { ja: "実装と運用まで一気通貫", en: "End-to-end delivery" },
          body: {
            ja: "仕入れ・ライセンス提供から構築・移行・研修まで同じチームが担当。ベンダー間の調整コストを抑えます。",
            en: "One squad from procurement and licensing through build, migration, and training—less vendor coordination tax.",
          },
        },
        {
          title: { ja: "多様な製品への定着支援", en: "Adoption across your stack" },
          body: {
            ja: "M365・GWS・SaaS・AI製品など、非エンジニア向けのわかりやすい研修設計。現場の「使える」レベルまで落とし込みます。",
            en: "Practical workshops for M365, Google Workspace, SaaS, and AI products—designed for non-engineers, not slide decks.",
          },
        },
      ],
    },
    faq: {
      heading: { ja: "よくあるご質問", en: "FAQ" },
      items: [
        {
          q: { ja: "どのような製品の仕入れ・提供に対応していますか？", en: "What products can you procure?" },
          a: {
            ja: "Microsoft 365、Google Workspace、各種SaaS、AI製品（Copilot・Gemini等）、セキュリティ製品、クラウド（AWS/GCP/Azure）のライセンス・利用契約に対応しています。PC・IT機器は順次対応予定です。",
            en: "Microsoft 365, Google Workspace, SaaS, AI products (Copilot, Gemini, etc.), security products, and cloud (AWS/GCP/Azure) licenses. PC and hardware supply coming soon.",
          },
        },
        {
          q: { ja: "PC・IT機器の仕入れ・販売はいつから対応しますか？", en: "When will PC and hardware supply be available?" },
          a: {
            ja: "法人向けPC・周辺機器の仕入れ・販売を順次開始予定です。クラウド・SaaS環境と合わせた端末構成のご相談も承ります。詳細はお問い合わせください。",
            en: "Corporate PC and peripheral procurement and sales are launching soon. We can also advise on device configurations aligned with your cloud and SaaS stack—contact us for details.",
          },
        },
        {
          q: { ja: "既存のITベンダーとの協業は可能ですか？", en: "Can you work with our existing vendors?" },
          a: {
            ja: "可能です。お客様の既存体制を尊重しつつ、不足する設計・実装領域を補完する形で参画します。",
            en: "We complement your existing vendors—filling design and build gaps without stepping on toes.",
          },
        },
        {
          q: { ja: "導入後のサポートはありますか？", en: "Post-go-live support?" },
          a: {
            ja: "運用保守契約やスポット支援に対応。内製化を目指す場合は、段階的なナレッジ移転プランも提案します。",
            en: "Managed support or spot help—and phased knowledge transfer when you want to in-house it.",
          },
        },
        {
          q: { ja: "ライセンス選定も支援してもらえますか？", en: "Can you help with license selection?" },
          a: {
            ja: "はい。利用部門と機能要件から最適なプランを整理し、過剰ライセンスを抑えた構成を提案します。",
            en: "Yes—we map departments and features to right-sized plans and avoid over-licensing.",
          },
        },
        {
          q: { ja: "セキュリティ要件が厳しい業界でも対応できますか？", en: "Can you meet strict security requirements?" },
          a: {
            ja: "金融・公共・製造など、ログ保管・アクセス制御・データ所在地の要件に沿った設計に対応します。",
            en: "We design for finance, public sector, and manufacturing—logging, access, and data residency included.",
          },
        },
        {
          q: { ja: "移行中のダウンタイムはどの程度想定しますか？", en: "How much downtime should we expect?" },
          a: {
            ja: "ワークロードごとに切り替え方式を設計し、夜間・休日の短時間切替や並行稼働で影響を最小化します。",
            en: "We plan per-workload cutovers—night/weekend windows and parallel runs to minimize impact.",
          },
        },
        {
          q: { ja: "研修はオンラインでも実施できますか？", en: "Can training be delivered online?" },
          a: {
            ja: "可能です。ライブ研修・録画コンテンツ・管理者向けハンズオンの組み合わせで定着を支援します。",
            en: "Yes—live sessions, recordings, and admin workshops combined for adoption.",
          },
        },
        {
          q: { ja: "AI連携を見据えたデータ設計とは何ですか？", en: "What is AI-ready data design?" },
          a: {
            ja: "将来のRAG・分析利用を想定し、データ分類・権限・メタデータを最初から整えることです。",
            en: "Classifying data, permissions, and metadata up front for future RAG and analytics use.",
          },
        },
        {
          q: { ja: "見積もりの進め方を教えてください。", en: "How do estimates work?" },
          a: {
            ja: "現状ヒアリング後、フェーズごとの工数とオプションを提示します。初回相談は無料です。",
            en: "After discovery we share phased effort and options—the first consult is free.",
          },
        },
        {
          q: { ja: "複数拠点・海外拠点がある場合も対応できますか？", en: "Multi-site and global rollouts?" },
          a: {
            ja: "テナント設計・ネットワーク・タイムゾーンを考慮した段階展開プランを策定します。",
            en: "We plan phased rollouts accounting for tenant design, networking, and time zones.",
          },
        },
      ],
    },
    cta: {
      heading: { ja: "製品選定・導入の第一歩を", en: "Take the first step" },
      body: {
        ja: "必要な製品や現状の課題をお聞かせください。仕入れから導入・定着まで、最適なプランをご提案します。",
        en: "Tell us what you need and what's blocking you—we'll propose a plan from procurement through adoption.",
      },
      button: { ja: "SaaS・クラウド導入について相談", en: "Discuss SaaS & cloud" },
    },
  },
  drone: {
    slug: "drone",
    canonical: "https://www.napire.co.jp/drone.html",
    pageTitle: {
      ja: "自社AIドローン | 株式会社NAPIRE",
      en: "In-house AI Drone | NAPIRE Inc.",
    },
    metaDescription: {
      ja: "NAPIREの自社AIドローン。白紙の機体から設計し、飛行制御から認識AI・地上系まで一気通貫。農業・監視・物流など現場で動く自律飛行システムを開発します。",
      en: "NAPIRE's in-house AI drones—airframe to flight control, perception, and ground ops. Autonomous systems built for agriculture, security, logistics, and more.",
    },
    keywords: {
      ja: "AIドローン,自社開発,自律飛行,Computer Vision,エッジ推論,組み込み,NAPIRE",
      en: "AI drone,in-house,autonomous flight,computer vision,edge inference,NAPIRE",
    },
    hero: {
      eyebrow: { ja: "Product / AI Drone", en: "Product / AI Drone" },
      title: { ja: "ドローンは、白紙の機体から。", en: "Drones from a blank airframe." },
      lead: {
        ja: "キットを組むのではなく、用途から機体・推力・センサを設計。AI・IT企業として培った実装力で、飛行制御から認識AI・地上系まで一気通貫で開発します。",
        en: "We don't assemble kits—we design frames, thrust, and sensors for the mission. As an AI & IT builder, we ship flight control, perception, and ground ops end to end.",
      },
      cta: { ja: "導入・共同開発を相談", en: "Discuss a build" },
    },
    questions: {
      heading: { ja: "こんな違和感、ありませんか？", en: "Sound familiar?" },
      items: [
        {
          q: {
            ja: "既製品ドローンでは、現場の要件を満たせない",
            en: "Off-the-shelf drones miss our field requirements",
          },
          a: {
            ja: "用途から機体サイズ・推力・センサ構成を逆算し、認識精度と航続・風耐性まで含めて一貫設計します。",
            en: "We size frames, thrust, and sensors from the mission—accuracy, endurance, and wind tolerance designed together.",
          },
        },
        {
          q: {
            ja: "デモ飛行は成功したが、本番運用で破綻した",
            en: "The demo flew—production ops fell apart",
          },
          a: {
            ja: "地上局、ログ、OTA、点検・交換を最初から織り込み。長期運用を前提としたモジュール構成で実装します。",
            en: "Ground station, logging, OTA, and field maintenance woven in from day one—not bolted on after the demo.",
          },
        },
        {
          q: {
            ja: "ハードとソフトを別ベンダーに任せ、連携がうまくいかない",
            en: "Hardware and software vendors don't meet in the middle",
          },
          a: {
            ja: "機体・制御・認識AIを同じチームが担当。推力モデルやIMUノイズを踏まえた推論パイプラインを無理なく結線します。",
            en: "One team owns airframe, control, and perception—wiring inference to real thrust and IMU behavior.",
          },
        },
      ],
    },
    services: {
      heading: { ja: "提供領域", en: "What we deliver" },
      items: [
        {
          title: { ja: "機体・ハードウェア設計", en: "Airframe & hardware design" },
          desc: {
            ja: "機体選定〜カスタムフレーム、プロペラ、ESC、バッテリー、各種センサのマウントまでを一つの設計として進行。風・高度・航続で逆算します。",
            en: "Frame selection or custom builds, propulsion, power, and sensor mounts as one system—traded against wind, altitude, and endurance.",
          },
        },
        {
          title: { ja: "飛行制御・フェイルセーフ", en: "Flight control & failsafes" },
          desc: {
            ja: "安定化・高度保持・帰還ロジックを実機の慣性・推力モデルに合わせてチューニング。物理制約の中で動く制御ループを実装します。",
            en: "Stabilization, altitude hold, and return-to-home tuned to real inertia and thrust—not simulator defaults.",
          },
        },
        {
          title: { ja: "認識AI・エッジ推論", en: "Perception AI & edge inference" },
          desc: {
            ja: "画像検知・追従・マッピングを、搭載カメラの解像度・FPS・露出前提で設計。農業・監視・点検など用途別のモデルと後処理を構築。",
            en: "Detection, tracking, and mapping scoped to your payload—mission-specific models for agriculture, security, and inspection.",
          },
        },
        {
          title: { ja: "地上局・運用基盤", en: "Ground station & ops platform" },
          desc: {
            ja: "地上局UI、ログ・遥測ストリーミング、OTA更新、API連携まで含めた運用基盤。現場オペレーターが使い続けられる形に仕上げます。",
            en: "Ground UIs, telemetry, logging, OTA, and API integrations—built for operators who run missions daily.",
          },
        },
      ],
    },
    process: {
      heading: { ja: "開発の流れ", en: "How we work" },
      steps: [
        {
          title: { ja: "01 ミッション定義", en: "01 Define the mission" },
          desc: {
            ja: "飛行空域・航続・ペイロード・評価指標を整理。PoCと本番運用の境界を明確にし、ROIが見えるスコープに絞り込みます。",
            en: "Align on airspace, endurance, payloads, and success metrics—scoped PoCs with a clear path to production.",
          },
        },
        {
          title: { ja: "02 機体・センサ設計", en: "02 Airframe & sensors" },
          desc: {
            ja: "機体サイズ・推力・センサ構成を用途から設計。振動・露出・配線まで含めた実機制約を先に取り込みます。",
            en: "Size frames, thrust, and sensors for the mission—vibration, exposure, and wiring locked early.",
          },
        },
        {
          title: { ja: "03 制御・認識の統合実装", en: "03 Integrate control & perception" },
          desc: {
            ja: "飛行制御ループと推論パイプラインを結線。ベンチ検証・実機チューニングを経て、現場条件に耐える統合システムを構築します。",
            en: "Wire control loops and inference pipelines—bench tests and field tuning until the stack survives real conditions.",
          },
        },
        {
          title: { ja: "04 実証飛行・運用移行", en: "04 Field validation & handoff" },
          desc: {
            ja: "実証飛行と評価指標の検証。運用手順・点検サイクル・保守体制まで含めて、本番運用に移行します。",
            en: "Validation flights against success criteria—runbooks, inspection cycles, and maintenance for production ops.",
          },
        },
      ],
    },
    why: {
      heading: { ja: "NAPIREが選ばれる理由", en: "Why NAPIRE" },
      items: [
        {
          title: { ja: "ハード制約がAIの精度を決める", en: "Hardware sets AI accuracy" },
          body: {
            ja: "カメラの露出・振動・FPS——機体に合わせてモデルと後処理を設計できるから、現場で使える認識が出せます。",
            en: "Exposure, vibration, FPS—models and post-processing scoped to the payload you actually carry.",
          },
        },
        {
          title: { ja: "制御と認識を同じチームで", en: "One team for control & perception" },
          body: {
            ja: "推力モデル・IMUノイズを知っているから、安定化と推論パイプラインを無理なく結線できます。",
            en: "Knowing thrust and IMU noise, we wire stabilization and inference without heroic hacks.",
          },
        },
        {
          title: { ja: "運用で死なない設計", en: "Built for long-running ops" },
          body: {
            ja: "地上局、ログ、OTA、点検・交換を見据えたモジュール構成。デモで終わらない形にします。",
            en: "Ground station, logging, OTA, and field maintenance—layouts meant to outlive the demo.",
          },
        },
      ],
    },
    faq: {
      heading: { ja: "よくあるご質問", en: "FAQ" },
      items: [
        {
          q: { ja: "既製品ドローンとの違いは何ですか？", en: "How is this different from off-the-shelf drones?" },
          a: {
            ja: "用途から機体・センサ・推論パイプラインを一貫設計するため、汎用機では難しい精度と運用性を狙えます。",
            en: "We co-design airframe, sensors, and inference for the mission—accuracy and ops off-the-shelf kits can't match.",
          },
        },
        {
          q: { ja: "共同開発は可能ですか？", en: "Do you offer co-development?" },
          a: {
            ja: "はい。PoCから量産前の試作まで、お客様の用途に合わせた共同開発プロジェクトに対応します。",
            en: "Yes—from PoC through pre-production prototypes tailored to your mission.",
          },
        },
        {
          q: { ja: "対応している飛行規制・認証について教えてください。", en: "What about regulations and certification?" },
          a: {
            ja: "用途・飛行空域に応じた申請要件を整理し、必要な試験・ドキュメント整備を支援します。",
            en: "We map airspace and mission rules and support required testing and documentation.",
          },
        },
        {
          q: { ja: "農業以外の産業でも利用できますか？", en: "Beyond agriculture?" },
          a: {
            ja: "監視、インフラ点検、物流、災害対応など、センサと飛行ロジックを差し替えて対応します。",
            en: "Security, infrastructure inspection, logistics, disaster response—swap sensors and flight logic per mission.",
          },
        },
        {
          q: { ja: "AIモデルはクラウド推論ですか？機上推論ですか？", en: "Cloud or onboard inference?" },
          a: {
            ja: "レイテンシ・通信・電力の制約から最適なハイブリッド構成を提案します。多くの現場判断はエッジで処理します。",
            en: "We propose hybrid stacks—most field decisions run at the edge given latency, bandwidth, and power limits.",
          },
        },
        {
          q: { ja: "開発期間と費用の目安は？", en: "Typical timeline and cost?" },
          a: {
            ja: "PoCは2〜4ヶ月、本番向け試作は要件により異なります。初回相談でスコープと概算をお伝えします。",
            en: "PoCs often run 2–4 months; production prototypes vary—we scope and estimate in the first consult.",
          },
        },
        {
          q: { ja: "既存の地上システムと連携できますか？", en: "Integration with existing ground systems?" },
          a: {
            ja: "API・MQTT・既存ダッシュボードとの連携を設計。運用チームが使い続けられる地上局UIも開発します。",
            en: "We wire APIs, MQTT, and dashboards—and build ground UIs operators will actually use.",
          },
        },
        {
          q: { ja: "保守・点検・部品交換はどうなりますか？", en: "Maintenance and parts?" },
          a: {
            ja: "モジュール交換・OTA更新・点検手順を最初から設計。長期運用を前提とした構成にします。",
            en: "Modular swaps, OTA, and inspection runbooks—built for long-running operations.",
          },
        },
        {
          q: { ja: "データの取り扱いとセキュリティは？", en: "Data handling and security?" },
          a: {
            ja: "映像・ログの保存場所、暗号化、アクセス権限を用途に合わせて設計。閉域ネットワークにも対応します。",
            en: "We design storage, encryption, and access per mission—including closed networks.",
          },
        },
        {
          q: { ja: "デモ飛行や技術検証は依頼できますか？", en: "Can we request a demo flight or technical validation?" },
          a: {
            ja: "可能です。目的と評価指標を整理した上で、限定スコープの実証飛行・ベンチ検証を提案します。",
            en: "Yes—scoped demo flights and bench tests with clear success criteria.",
          },
        },
      ],
    },
    cta: {
      heading: { ja: "共同開発・導入のご相談", en: "Co-development & rollout" },
      body: { ja: "PoCから本番運用まで。機体要件からお聞かせください。", en: "From PoC to ops—tell us your mission profile." },
      button: { ja: "ドローンについて相談", en: "Talk drones" },
    },
  },
  shared: {
    nav: {
      home: { ja: "ホーム", en: "Home" },
      aiDx: { ja: "AI・DX導入支援", en: "AI & DX" },
      saasCloud: { ja: "SaaS・クラウド", en: "SaaS & Cloud" },
      contact: { ja: "お問い合わせ", en: "Contact" },
    },
    breadcrumb: {
      home: { ja: "ホーム", en: "Home" },
    },
    backHome: { ja: "トップへ戻る", en: "Back to home" },
  },
}
